import { ChevronLeft, LocateFixed, MapPin, PackageCheck, Plus, ShoppingBag, Store, Truck, WalletCards, X } from "lucide-react";
import { FormEvent, lazy, Suspense, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { LinkButton } from "../components/Button";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { ActionRow, EmptyState } from "../components/UI";
import {
  ApiError,
  createAddress,
  createCartOrder,
  formatCurrency,
  getAddresses,
  getPaymentMethods,
  getShippingRates,
  getUserId,
  initiatePayment,
  resolveAssetUrl,
  type AddressData,
  type AddressRecord,
  type PaymentMethodsConfig,
  type ShippingMethod,
  type TaxBreakdown
} from "../lib/api";
import { cn } from "../lib/utils";
import { useAppState } from "../state/AppState";

const STLViewer = lazy(() => import("../components/STLViewer").then((module) => ({ default: module.STLViewer })));

type OutletContext = {
  openLogin: (options?: { onCancel?: () => void }) => void;
};

function PhonePeMark() {
  return <span className="phonepe-mark" aria-hidden="true">पे</span>;
}

type AddressFormState = {
  name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
};

type AddressFieldKey = keyof AddressFormState;
type AddressFieldErrors = Partial<Record<AddressFieldKey, string>>;

const REQUIRED_ADDRESS_FIELDS: AddressFieldKey[] = ["name", "phone", "line1", "city", "pincode"];
const PENDING_PAYMENT_KEY = "pd_pending_payment";
const ADDRESS_FIELD_LABELS: Record<AddressFieldKey, string> = {
  name: "name",
  phone: "phone number",
  line1: "address line",
  line2: "landmark",
  city: "city",
  state: "state",
  pincode: "PIN code"
};

function buildDefaultAddress(userName?: string, phone?: string): AddressFormState {
  return {
    name: userName || "",
    phone: phone || "",
    line1: "",
    line2: "",
    city: "Bengaluru",
    state: "Karnataka",
    pincode: ""
  };
}

function toAddressData(form: AddressFormState): AddressData {
  return {
    name: form.name.trim(),
    phone: form.phone.trim(),
    line1: form.line1.trim(),
    line2: form.line2.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    pincode: form.pincode.trim()
  };
}

function withUserAddressDefaults(address: AddressData, user?: { full_name?: string; phone?: string } | null): AddressData {
  return {
    ...address,
    name: address.name || user?.full_name || "",
    phone: address.phone || user?.phone || ""
  };
}

function addressLabel(address?: AddressData | null) {
  if (!address) return "";
  return [address.city, address.state].filter(Boolean).join(", ");
}

function addressLines(address?: AddressData | null) {
  if (!address) return [];
  return dedupeParts([
    address.name,
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(", ") + (address.pincode ? ` - ${address.pincode}` : "")
  ]);
}

function addressDetailLines(address?: AddressData | null) {
  if (!address) return [];
  return dedupeParts([
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(", ") + (address.pincode ? ` - ${address.pincode}` : "")
  ]);
}

function cartItemImageUrl(item: Record<string, unknown>) {
  const image = typeof item.image === "string" ? item.image.trim() : "";
  return image ? resolveAssetUrl(image) : "";
}

function cartItemHasStl(item: Record<string, unknown>) {
  return Boolean(item.stl_file || item.stlFile || item.model_file_url || item.file_url);
}

function cartItemStlUrl(item: Record<string, unknown>) {
  const stl = item.stl_file || item.stlFile || item.model_file_url || item.file_url;
  return typeof stl === "string" && stl.trim() ? resolveAssetUrl(stl) : "";
}

function formatCheckoutCurrency(value: number | string | undefined): string {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function shippingMethodDetail(method: ShippingMethod) {
  if (method.safe_message) return method.safe_message;
  if (method.etd) return `Estimated delivery by ${method.etd}`;
  if (method.estimated_delivery_days !== undefined && method.estimated_delivery_days !== null && String(method.estimated_delivery_days).trim()) {
    const days = String(method.estimated_delivery_days).trim();
    return `${days} day${days === "1" ? "" : "s"} delivery`;
  }
  if (method.fallback_eta) return method.fallback_eta;
  if (method.courier_name) return method.courier_name;
  return "Shipping option";
}

function addressComplete(address?: AddressData | null) {
  if (!address) return false;
  return Boolean(address.name && /^\d{10}$/.test(address.phone || "") && address.line1 && address.city && /^\d{6}$/.test(address.pincode || ""));
}

function missingAddressFields(address?: AddressData | null) {
  const errors = buildAddressErrors(address || null);
  return (Object.keys(errors) as AddressFieldKey[]).map((field) => ADDRESS_FIELD_LABELS[field]);
}

function buildAddressErrors(address?: Partial<Record<AddressFieldKey, string | undefined>> | null): AddressFieldErrors {
  const errors: AddressFieldErrors = {};
  for (const field of REQUIRED_ADDRESS_FIELDS) {
    if (!String(address?.[field] || "").trim()) {
      errors[field] = "Required";
    }
  }
  if (address?.phone && !/^\d{10}$/.test(address.phone)) {
    errors.phone = "Enter a 10 digit phone number";
  }
  if (address?.pincode && !/^\d{6}$/.test(address.pincode)) {
    errors.pincode = "Enter a 6 digit pincode";
  }
  return errors;
}

function reconcileAddressErrors(
  address: Partial<Record<AddressFieldKey, string | undefined>>,
  currentErrors: AddressFieldErrors
): AddressFieldErrors {
  const nextErrors: AddressFieldErrors = {};
  for (const field of Object.keys(currentErrors) as AddressFieldKey[]) {
    if (!String(address[field] || "").trim()) {
      nextErrors[field] = currentErrors[field] || "Required";
    }
  }
  return nextErrors;
}

function pickFirstAddress(addresses: AddressRecord[], types: string[]) {
  return addresses.find((entry) => types.includes(entry.type || "") || !entry.type) || null;
}

function sameAddress(left?: AddressData | null, right?: AddressData | null) {
  if (!left || !right) return false;
  if (addressIdentityKey(left) === addressIdentityKey(right)) return true;
  const sameLocation = [
    left.name,
    left.line1,
    left.line2,
    left.city,
    left.state,
    left.pincode
  ].map(normalizeAddressPart).join("|") === [
    right.name,
    right.line1,
    right.line2,
    right.city,
    right.state,
    right.pincode
  ].map(normalizeAddressPart).join("|");
  return sameLocation && (!left.phone || !right.phone || normalizeAddressPart(left.phone) === normalizeAddressPart(right.phone));
}

function normalizeAddressPart(value?: string | null) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function addressIdentityKey(address?: AddressData | null) {
  if (!address) return "";
  return [
    address.name,
    address.phone,
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.pincode
  ].map(normalizeAddressPart).join("|");
}

function uniqueAddressRecords(records: AddressRecord[]) {
  const seen = new Set<string>();
  return records.filter((record) => {
    const key = addressIdentityKey(record.address_data);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function addressKindLabel(entry: AddressRecord, index: number) {
  const explicitLabel = String((entry.address_data as AddressData & { label?: string }).label || "").trim();
  if (explicitLabel) return explicitLabel;
  if (entry.type === "billing") return "Billing";
  return index === 0 ? "Saved address" : `Saved address ${index + 1}`;
}

function shippingPriceLabel(amount: number | string | undefined | null) {
  const price = Number(amount || 0);
  return price <= 0 ? "Free" : formatCurrency(price);
}

function methodIdentity(method?: ShippingMethod) {
  if (!method) return "";
  return [
    Number(method.amount || 0).toFixed(2),
    shippingMethodDetail(method).toLowerCase(),
    String(method.etd || "").toLowerCase(),
    String(method.estimated_delivery_days || "").toLowerCase()
  ].join("|");
}

function isDuplicateFastMethod(method: ShippingMethod, standard?: ShippingMethod) {
  return method.method === "fast" && Boolean(standard) && methodIdentity(method) === methodIdentity(standard);
}

function shippingBadges(method: ShippingMethod, index: number) {
  const badges: string[] = [];
  if (index === 0 && method.method !== "store_pickup") badges.push("Recommended");
  if (method.method === "fast") badges.push("Fastest");
  if (Number(method.amount || 0) <= 0) badges.push("Free");
  return badges;
}

function dedupeParts(parts: Array<string | undefined | null>) {
  const seen = new Set<string>();
  return parts
    .map((part) => String(part || "").trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function CheckoutPage() {
  const { cart, clearCart, user, addToast } = useAppState();
  const { openLogin } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const authPromptedRef = useRef(false);
  const shippingSectionRef = useRef<HTMLDivElement>(null);
  const shouldScrollShippingRef = useRef(false);
  const subtotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);

  const [addresses, setAddresses] = useState<AddressRecord[]>([]);
  const [shippingAddress, setShippingAddress] = useState<AddressData | null>(null);
  const [billingAddress, setBillingAddress] = useState<AddressData | null>(null);
  const [addressPickerOpen, setAddressPickerOpen] = useState(false);
  const [shippingFormOpen, setShippingFormOpen] = useState(false);
  const [billingFormOpen, setBillingFormOpen] = useState(false);
  const [shippingForm, setShippingForm] = useState<AddressFormState>(() => buildDefaultAddress(user?.full_name, user?.phone));
  const [billingForm, setBillingForm] = useState<AddressFormState>(() => buildDefaultAddress(user?.full_name, user?.phone));
  const [shippingFieldErrors, setShippingFieldErrors] = useState<AddressFieldErrors>({});
  const [billingFieldErrors, setBillingFieldErrors] = useState<AddressFieldErrors>({});
  const [savingAddress, setSavingAddress] = useState<"shipping" | "billing" | null>(null);
  const [saveShipping, setSaveShipping] = useState(true);
  const [saveBilling, setSaveBilling] = useState(true);
  const [billingSame, setBillingSame] = useState(true);
  const [paymentConfig, setPaymentConfig] = useState<PaymentMethodsConfig>({});
  const [paymentMethod, setPaymentMethod] = useState("");
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [taxByMethod, setTaxByMethod] = useState<Record<string, TaxBreakdown>>({});
  const [selectedShippingMethod, setSelectedShippingMethod] = useState("");
  const [shippingStatus, setShippingStatus] = useState("Select a shipping address to see available shipping methods.");
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingQuoteSource, setShippingQuoteSource] = useState("checkout");
  const [locating, setLocating] = useState<"shipping" | "billing" | null>(null);
  const [placingOrder, setPlacingOrder] = useState(false);

  function selectShippingAddress(address: AddressData, shouldScroll = true) {
    shouldScrollShippingRef.current = shouldScroll;
    setShippingAddress(withUserAddressDefaults(address, user));
    setShippingFieldErrors({});
    setSelectedShippingMethod("");
    setAddressPickerOpen(false);
  }

  useEffect(() => {
    if (!user) {
      if (!authPromptedRef.current) {
        authPromptedRef.current = true;
        openLogin({ onCancel: () => navigate("/quote", { replace: true }) });
      }
      return;
    }
    if (!user.token) {
      if (!authPromptedRef.current) {
        authPromptedRef.current = true;
        addToast("Please log in again to continue checkout.", "error");
        openLogin({ onCancel: () => navigate("/quote", { replace: true }) });
      }
      return;
    }
    authPromptedRef.current = false;
    const userId = getUserId(user);
    if (!userId) return;

    let cancelled = false;
    Promise.all([
      getAddresses(userId, user.token).catch(() => []),
      getPaymentMethods().catch(() => ({} as PaymentMethodsConfig))
    ]).then(([addressData, paymentData]) => {
      if (cancelled) return;
      setAddresses(addressData);
      setPaymentConfig(paymentData);
      setPaymentMethod((current) => {
        if (current) return current;
        if (paymentData.phonepe_enabled && paymentData.phonepe_ready) return "phonepe";
        if (paymentData.cod_enabled) return "manual";
        return "";
      });

      const firstShipping = pickFirstAddress(addressData, ["shipping", "both"]);
      const firstBilling = pickFirstAddress(addressData, ["billing", "both"]);
      if (firstShipping) {
        setShippingAddress((current) => current || withUserAddressDefaults(firstShipping.address_data, user));
      }
      if (firstBilling) {
        setBillingAddress((current) => current || withUserAddressDefaults(firstBilling.address_data, user));
      }
      if (!firstShipping) setShippingFormOpen(true);
      if (!firstBilling) setBillingFormOpen(true);
    });

    return () => {
      cancelled = true;
    };
  }, [addToast, navigate, openLogin, user]);

  useEffect(() => {
    if (!user?.token || !cart.length || !shippingAddress?.pincode) {
      setShippingMethods([]);
      setTaxByMethod({});
      setSelectedShippingMethod("");
      setShippingStatus("Select a shipping address to see available shipping methods.");
      return;
    }

    const payloadItems = cart.map((item) => {
      const { quote, image, ...rest } = item;
      return {
        ...rest,
        image,
        stl_file: item.stl_file || item.stlFile,
        quote_options: item.quote_options || quote?.quote_options
      };
    });

    let cancelled = false;
    setShippingLoading(true);
    setShippingStatus("Fetching shipping rates for the selected address...");
    getShippingRates(payloadItems, subtotal, shippingAddress, false, user.token)
      .then((response) => {
        if (cancelled) return;
        const ordered = ["standard", "fast", "store_pickup"];
        const methods = ordered
          .map((code) => response.methods?.[code])
          .filter(Boolean) as ShippingMethod[];
        const extras = Object.values(response.methods || {}).filter((item) => !ordered.includes(item.method));
        const merged = [...methods, ...extras];
        setShippingMethods(merged);
        setTaxByMethod(response.tax_by_method || {});
        setSelectedShippingMethod((current) => (
          current && merged.some((item) => item.method === current) ? current : merged[0]?.method || ""
        ));
        setShippingQuoteSource(response.shipping_quote_source || "checkout");
        const hasLiveRates = merged.some((method) => method.source === "shiprocket");
        setShippingStatus(merged.length
          ? hasLiveRates
            ? "Shipping quote ready."
            : "Using fallback delivery rates. Live shipping rates are unavailable."
          : "No shipping methods are available for the selected address.");
        if (merged.length && shouldScrollShippingRef.current) {
          shouldScrollShippingRef.current = false;
          requestAnimationFrame(() => {
            shippingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            shippingSectionRef.current?.focus({ preventScroll: true });
          });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setShippingMethods([]);
        setTaxByMethod({});
        setSelectedShippingMethod("");
        const message = error instanceof ApiError && error.status >= 500
          ? "Live shipping rates are unavailable right now."
          : error instanceof Error
            ? error.message
            : "Failed to fetch shipping rates.";
        setShippingStatus(message);
      })
      .finally(() => {
        if (!cancelled) setShippingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cart, shippingAddress, subtotal, user?.token]);

  async function applyCurrentLocation(kind: "shipping" | "billing") {
    if (!navigator.geolocation) {
      addToast("Current location is not available in this browser.", "error");
      return;
    }
    setLocating(kind);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`);
          const data = await response.json();
          const address = data?.address || {};
          const city = address.city || address.town || address.village || address.municipality || "Bengaluru";
          const line1 = dedupeParts([
            [address.house_number, address.road].filter(Boolean).join(" "),
            address.neighbourhood,
            address.suburb,
            address.city_district
          ])[0] || city || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
          const next = {
            line1,
            line2: dedupeParts([address.suburb, address.neighbourhood, address.city_district]).filter((part) => part !== line1).join(", "),
            city,
            state: address.state || "Karnataka",
            pincode: String(address.postcode || "").replace(/\D+/g, "").slice(0, 6)
          };
          if (kind === "shipping") {
            setShippingForm((current) => {
              const updated = { ...current, ...next };
              setShippingFieldErrors((existing) => reconcileAddressErrors(updated, existing));
              return updated;
            });
          } else {
            setBillingForm((current) => {
              const updated = { ...current, ...next };
              setBillingFieldErrors((existing) => reconcileAddressErrors(updated, existing));
              return updated;
            });
          }
        } catch {
          addToast("Could not resolve your current address. Fill it manually.", "error");
        } finally {
          setLocating(null);
        }
      },
      () => {
        setLocating(null);
        addToast("Could not access your current location.", "error");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  async function saveAddressIfNeeded(address: AddressData, type: "shipping" | "billing" | "both") {
    if (!user?.token) return null;
    const userId = getUserId(user);
    if (!userId) return null;
    const existing = addresses.find((entry) => sameAddress(entry.address_data, address) && (entry.type === type || entry.type === "both" || type === "both"));
    if (existing) return existing;
    const created = await createAddress(userId, user.token, address, type);
    setAddresses((current) => [...current, created]);
    return created;
  }

  async function confirmShippingAddress() {
    const next = toAddressData(shippingForm);
    const nextErrors = buildAddressErrors(next);
    setShippingFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      addToast("Complete the highlighted shipping fields.", "error");
      return;
    }
    try {
      setSavingAddress("shipping");
      if (saveShipping) {
        const saved = await saveAddressIfNeeded(next, billingSame ? "both" : "shipping");
        selectShippingAddress(saved?.address_data || next);
        addToast("Shipping address saved", "success");
      } else {
        selectShippingAddress(next);
      }
      setShippingFieldErrors({});
      setShippingFormOpen(false);
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save shipping address.", "error");
    } finally {
      setSavingAddress(null);
    }
  }

  async function confirmBillingAddress() {
    const next = toAddressData(billingForm);
    const nextErrors = buildAddressErrors(next);
    setBillingFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      addToast("Complete the highlighted billing fields.", "error");
      return;
    }
    try {
      setSavingAddress("billing");
      if (saveBilling) {
        const saved = await saveAddressIfNeeded(next, "billing");
        setBillingAddress(saved?.address_data || next);
        addToast("Billing address saved", "success");
      } else {
        setBillingAddress(next);
      }
      setBillingFieldErrors({});
      setBillingFormOpen(false);
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save billing address.", "error");
    } finally {
      setSavingAddress(null);
    }
  }

  async function submitOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (placingOrder) return;
    if (!user) {
      openLogin({ onCancel: () => navigate("/quote", { replace: true }) });
      return;
    }
    if (!user.token) {
      addToast("Please log in again before placing the order.", "error");
      openLogin({ onCancel: () => navigate("/quote", { replace: true }) });
      return;
    }
    const userId = getUserId(user);
    if (!userId) {
      addToast("Session is missing a user ID. Please log in again.", "error");
      return;
    }
    if (!shippingAddress || !addressComplete(shippingAddress)) {
      addToast("Please select or enter a shipping address.", "error");
      return;
    }
    if (!selectedShippingMethod) {
      addToast("Please choose a shipping method.", "error");
      return;
    }
    if (!paymentMethod) {
      addToast("Please choose a payment method.", "error");
      return;
    }
    const selectedMethod = shippingMethods.find((item) => item.method === selectedShippingMethod);
    if (!selectedMethod) {
      addToast("Please choose a valid shipping method.", "error");
      return;
    }
    const effectiveBilling = billingSame ? shippingAddress : billingAddress;
    if (!effectiveBilling || !addressComplete(effectiveBilling)) {
      addToast("Please select or enter a billing address.", "error");
      return;
    }

    const payloadItems = cart.map((item) => {
      const { quote, source, image, stlFile, ...rest } = item;
      return {
        ...rest,
        source,
        image,
        stl_file: item.stl_file || stlFile,
        quote_options: item.quote_options || quote?.quote_options
      };
    });

    setPlacingOrder(true);
    try {
      if (saveShipping && shippingFormOpen === false && !addresses.some((entry) => JSON.stringify(entry.address_data) === JSON.stringify(shippingAddress))) {
        await createAddress(userId, user.token, shippingAddress, billingSame ? "both" : "shipping");
      }
      if (!billingSame && saveBilling && billingAddress && !addresses.some((entry) => JSON.stringify(entry.address_data) === JSON.stringify(billingAddress))) {
        await createAddress(userId, user.token, billingAddress, "billing");
      }

      const order = await createCartOrder({
        user_id: userId,
        items: payloadItems,
        order_metadata: {
          source: "react-cart",
          shipping: {
            method: selectedMethod.method,
            label: selectedMethod.label,
            charged_amount: selectedMethod.amount,
            source: selectedMethod.source || shippingQuoteSource
          }
        },
        delivery_address: shippingAddress,
        billing_address: effectiveBilling,
        billing_same: billingSame,
        save_shipping: saveShipping,
        save_billing: saveBilling,
        subtotal,
        shipping_method: selectedMethod.method,
        shipping_amount: selectedMethod.amount,
        shipping_quote_source: selectedMethod.source || shippingQuoteSource,
        payment_method: paymentMethod,
        delivery: selectedMethod.amount,
        total
      }, user.token);

      if (paymentMethod !== "manual") {
        const payment = await initiatePayment(order.order_id, paymentMethod, user.token);
        const payUrl = payment.extra?.pay_page_url;
        if (!payUrl) {
          throw new Error(payment.extra?.note || `${payment.provider || "Payment"} is not ready. Check gateway settings.`);
        }
        localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({
          order_id: order.order_id,
          display_id: order.display_id,
          gateway_order_id: payment.gateway_order_id,
          provider: payment.provider
        }));
        clearCart();
        window.location.assign(payUrl);
        return;
      }

      clearCart();
      addToast("Order Created", "success");
      window.location.assign("/orders");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to create order.", "error");
      setPlacingOrder(false);
    }
  }

  if (!user) return null;

  if (!cart.length) {
    return (
      <EmptyState icon={<PackageCheck size={24} />} title="No Items To Checkout" action={<LinkButton to="/quote">Get A Quote</LinkButton>}>
        Add a quote or product to continue.
      </EmptyState>
    );
  }

  const paymentOptions = [
    paymentConfig.cod_enabled ? { value: "manual", label: "Cash on delivery", icon: <WalletCards size={20} aria-hidden="true" />, description: "Pay when your order is delivered." } : null,
    paymentConfig.phonepe_enabled && paymentConfig.phonepe_ready ? { value: "phonepe", label: "PhonePe", icon: <PhonePeMark />, description: "Pay securely using PhonePe." } : null
  ].filter(Boolean) as Array<{ value: string; label: string; icon: ReactNode; description: string }>;

  const selectedShippingRate = shippingMethods.find((item) => item.method === selectedShippingMethod);
  const selectedTaxBreakdown = selectedShippingMethod ? taxByMethod[selectedShippingMethod] : undefined;
  const deliveryAmount = Number(selectedShippingRate?.amount || 0);
  const taxableAmount = selectedTaxBreakdown?.taxable_amount ?? subtotal + deliveryAmount;
  const total = selectedTaxBreakdown?.total ?? taxableAmount;
  const effectiveBilling = billingSame ? shippingAddress : billingAddress;
  const selectedAddressLines = addressLines(shippingAddress);
  const hasShippingAddress = addressComplete(shippingAddress);
  const hasBillingAddress = addressComplete(effectiveBilling);
  const hasSelectedShippingAddress = Boolean(shippingAddress && selectedAddressLines.length);
  const missingShippingFields = missingAddressFields(shippingAddress);
  const savedShippingAddresses = uniqueAddressRecords(addresses.filter((entry) => !entry.type || entry.type === "shipping" || entry.type === "both"));
  const savedBillingAddresses = uniqueAddressRecords(addresses.filter((entry) => entry.type === "billing" || entry.type === "both"));
  const selectedShippingRecord = savedShippingAddresses.find((entry) => sameAddress(entry.address_data, shippingAddress));
  const visibleShippingMethods = shippingMethods;
  const canPlaceOrder = Boolean(
    hasShippingAddress &&
    hasBillingAddress &&
    selectedShippingRate &&
    !shippingLoading &&
    paymentMethod
  );
  const checkoutButtonLabel = shippingLoading
    ? "Calculating delivery..."
    : !hasSelectedShippingAddress
      ? "Select address"
    : !hasShippingAddress
        ? `Missing ${missingShippingFields.join(", ")}`
      : !selectedShippingRate
        ? "Select delivery method"
        : !paymentMethod
          ? "Select payment method"
          : paymentMethod === "manual"
            ? "Place order"
            : `Pay ${formatCheckoutCurrency(total)}`;

  return (
    <section className="checkout-shell min-h-screen px-4 py-8 text-slate-950 dark:text-slate-50 sm:px-6 lg:px-8 lg:py-12">
      <div className="checkout-page-heading mb-8">
        <Button asChild variant="ghost" className="-ml-3 mb-3 text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-slate-50">
          <Link to="/cart">
            <ChevronLeft data-icon="inline-start" aria-hidden="true" />
            Back to cart
          </Link>
        </Button>
        <h1>Checkout</h1>
        <p>Confirm your address, delivery and payment details.</p>
      </div>
      <form className="checkout-page-layout" onSubmit={submitOrder}>
        <div className="checkout-stack">
          <Card className="checkout-card">
            <CardHeader className="checkout-card-header">
              <div>
                <CardTitle>Shipping address</CardTitle>
                <p>Choose where the order should be delivered.</p>
              </div>
            </CardHeader>

            <CardContent className="checkout-card-content">
              {selectedAddressLines.length ? (
                <div className="checkout-selected-address">
                  <div className="checkout-address-icon" aria-hidden="true">
                    <MapPin size={20} />
                  </div>
                  <div className="checkout-selected-address-copy">
                    {selectedAddressLines.map((line, index) => (
                      <span key={`${line}-${index}`} className={index === 0 ? "checkout-address-name" : undefined}>{line}</span>
                    ))}
                    {!hasShippingAddress && missingShippingFields.length ? (
                      <span className="checkout-address-warning">
                        Missing {missingShippingFields.join(", ")}. Update this address or use current location.
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="checkout-empty-address">Choose or add a shipping address.</div>
              )}

              <div className="checkout-address-actions">
                <div className="checkout-address-action-group">
                  {savedShippingAddresses.length ? (
                    <Button type="button" variant="outline" size="sm" className="checkout-address-action-btn" onClick={() => setAddressPickerOpen(true)}>
                      Change address
                    </Button>
                  ) : null}
                  <Button type="button" variant="outline" size="sm" className="checkout-address-action-btn" onClick={() => setShippingFormOpen((value) => !value)}>
                    <Plus data-icon="inline-start" aria-hidden="true" />
                    Add new address
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="checkout-address-action-btn current-location-btn"
                  onClick={() => {
                    setShippingFormOpen(true);
                    void applyCurrentLocation("shipping");
                  }}
                  disabled={locating === "shipping"}
                >
                  <LocateFixed data-icon="inline-start" aria-hidden="true" />
                  {locating === "shipping" ? "Locating..." : "Use current location"}
                </Button>
              </div>

            {!shippingFormOpen ? null : (
              <div className="checkout-address-form">
                <div className="checkout-inline-actions">
                  <Button type="button" variant="ghost" size="sm" className="current-location-btn" onClick={() => applyCurrentLocation("shipping")} disabled={locating === "shipping"}>
                    <LocateFixed data-icon="inline-start" aria-hidden="true" />
                    {locating === "shipping" ? "Locating..." : "Use current location"}
                  </Button>
                </div>
                <AddressFields value={shippingForm} onChange={setShippingForm} errors={shippingFieldErrors} setErrors={setShippingFieldErrors} />
                <label className="checkout-checkbox-row">
                  <Checkbox checked={saveShipping} onCheckedChange={(checked) => setSaveShipping(checked === true)} /> Save this address for future orders
                </label>
                <ActionRow className="checkout-form-actions">
                  <Button type="button" onClick={confirmShippingAddress} disabled={savingAddress === "shipping"}>{savingAddress === "shipping" ? "Saving..." : "Use this shipping address"}</Button>
                  <Button type="button" variant="ghost" onClick={() => setShippingFormOpen(false)}>Cancel</Button>
                </ActionRow>
              </div>
            )}

            <Separator />

            <div className="checkout-billing-toggle">
              <label className="checkout-checkbox-row strong">
                <Checkbox checked={billingSame} onCheckedChange={(checked) => setBillingSame(checked === true)} /> Billing address is same as shipping address
              </label>
            </div>

            {billingSame ? null : (
              <div className="collapsible-billing-section expanded">
                <h3>Billing address</h3>
                <div id="checkout-billing-addr-list" className="checkout-address-list">
                  {savedBillingAddresses.length ? (
                    savedBillingAddresses
                      .map((entry, index) => (
                        <label
                          key={entry.id}
                          className={`payment-method-option checkout-address-option${sameAddress(billingAddress, entry.address_data) ? " payment-method-option-selected" : ""}`}
                          onClick={() => {
                            setBillingAddress(withUserAddressDefaults(entry.address_data, user));
                            setBillingFieldErrors({});
                          }}
                        >
                          <input type="radio" name="saved-billing-address" checked={sameAddress(billingAddress, entry.address_data)} readOnly />
                          <div className="payment-method-copy">
                            <strong>{addressKindLabel(entry, index)}</strong>
                            <span>{entry.address_data.name || "Saved address"}</span>
                            <span>{entry.address_data.line1}</span>
                            <span>{addressLabel(entry.address_data)} {entry.address_data.pincode ? `- ${entry.address_data.pincode}` : ""}</span>
                          </div>
                        </label>
                      ))
                  ) : (
                    <div className="text-muted text-sm">No saved billing addresses. Use the form below.</div>
                  )}
                </div>
                <ActionRow className="checkout-billing-address-actions">
                    <Button type="button" variant="outline" onClick={() => setBillingFormOpen((value) => !value)}>+ Add billing address</Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                    className="current-location-btn"
                    onClick={() => {
                      setBillingFormOpen(true);
                      void applyCurrentLocation("billing");
                    }}
                    disabled={locating === "billing"}
                  >
                    <LocateFixed data-icon="inline-start" aria-hidden="true" />
                    {locating === "billing" ? "Locating..." : "Use current location"}
                  </Button>
                </ActionRow>
                {!billingFormOpen ? null : (
                  <div className="checkout-address-form">
                    <div className="checkout-inline-actions">
                      <Button type="button" variant="ghost" size="sm" className="current-location-btn" onClick={() => applyCurrentLocation("billing")} disabled={locating === "billing"}>
                        <LocateFixed data-icon="inline-start" aria-hidden="true" />
                        {locating === "billing" ? "Locating..." : "Use current location"}
                      </Button>
                    </div>
                    <AddressFields value={billingForm} onChange={setBillingForm} errors={billingFieldErrors} setErrors={setBillingFieldErrors} />
                    <label className="checkout-checkbox-row">
                      <Checkbox checked={saveBilling} onCheckedChange={(checked) => setSaveBilling(checked === true)} /> Save this address for future orders
                    </label>
                    <ActionRow className="checkout-form-actions">
                      <Button type="button" onClick={confirmBillingAddress} disabled={savingAddress === "billing"}>{savingAddress === "billing" ? "Saving..." : "Use this billing address"}</Button>
                      <Button type="button" variant="ghost" onClick={() => setBillingFormOpen(false)}>Cancel</Button>
                    </ActionRow>
                  </div>
                )}
              </div>
            )}
            </CardContent>
          </Card>

          <Card className="checkout-card checkout-delivery-panel" ref={shippingSectionRef} tabIndex={-1}>
            <CardHeader className="checkout-card-header">
              <div>
                <CardTitle>Delivery method</CardTitle>
                <p>Select how you would like to receive this order.</p>
              </div>
            </CardHeader>
            <CardContent>
              <div id="checkout-shipping-status" className={`checkout-status ${shippingMethods.length ? "" : "checkout-status-muted"}`}>{shippingStatus}</div>
              <div id="checkout-shipping-methods" className="checkout-method-list">
              {!visibleShippingMethods.length ? (
                shippingLoading ? (
                  <div className="checkout-skeleton-stack" aria-label="Loading shipping methods">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : null
              ) : (
                visibleShippingMethods.map((method, index) => (
                  <button
                    key={method.method}
                    type="button"
                    className={cn("checkout-choice-card checkout-shipping-option", selectedShippingMethod === method.method && "checkout-choice-selected")}
                    onClick={() => setSelectedShippingMethod(method.method)}
                  >
                    <div className="checkout-choice-main">
                      <div className="checkout-choice-icon" data-method={method.method} aria-hidden="true">
                        {method.method === "store_pickup" ? <Store size={20} /> : method.method === "fast" ? <PackageCheck size={20} /> : <Truck size={20} />}
                      </div>
                      <div className="payment-method-copy">
                        <strong>
                          {method.label}
                          {shippingBadges(method, index).map((badge) => (
                            <Badge variant="secondary" key={badge}>{badge}</Badge>
                          ))}
                        </strong>
                        <span>{shippingMethodDetail(method)}</span>
                      </div>
                    </div>
                    <strong className="checkout-method-price">{shippingPriceLabel(method.amount)}</strong>
                  </button>
                ))
              )}
              </div>
            </CardContent>
          </Card>

          <Card className="checkout-card checkout-payment-panel">
            <CardHeader className="checkout-card-header">
              <div>
                <CardTitle>Payment method</CardTitle>
                <p>Choose how you would like to pay.</p>
              </div>
            </CardHeader>
            <CardContent>
              <div id="checkout-payment-methods" className="checkout-payment-list">
              {!paymentOptions.length ? (
                <div className="text-muted text-sm">No payment methods are currently available.</div>
              ) : paymentOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={cn("checkout-choice-card checkout-payment-option", `checkout-payment-${option.value}`, paymentMethod === option.value && "checkout-choice-selected")}
                  onClick={() => setPaymentMethod(option.value)}
                >
                  <div className="checkout-choice-main">
                    <div className="payment-method-icon">
                      {option.icon}
                    </div>
                    <div className="payment-method-copy">
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </div>
                  </div>
                </button>
              ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {addressPickerOpen ? createPortal(
          <div className="checkout-address-sheet-backdrop" role="presentation" onClick={() => setAddressPickerOpen(false)}>
            <div className="checkout-address-sheet" role="dialog" aria-modal="true" aria-label="Choose shipping address" onClick={(event) => event.stopPropagation()}>
              <div className="checkout-address-sheet-header">
                <div>
                  <strong>Choose shipping address</strong>
                  <span>{savedShippingAddresses.length} saved address{savedShippingAddresses.length === 1 ? "" : "es"}</span>
                </div>
                <button type="button" className="checkout-icon-button" aria-label="Close address picker" onClick={() => setAddressPickerOpen(false)}>
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              <div id="checkout-addr-list" className="checkout-address-list checkout-address-sheet-list">
                {savedShippingAddresses.map((entry, index) => (
                  <label
                    key={entry.id}
                    className={`payment-method-option checkout-address-option${sameAddress(shippingAddress, entry.address_data) ? " payment-method-option-selected" : ""}`}
                    onClick={() => selectShippingAddress(entry.address_data)}
                  >
                    <input type="radio" name="saved-address" checked={sameAddress(shippingAddress, entry.address_data)} readOnly />
                    <span className="checkout-radio-indicator" aria-hidden="true" />
                    <div className="payment-method-copy">
                      <strong>{entry.address_data.name || addressKindLabel(entry, index)}</strong>
                      {addressDetailLines(entry.address_data).map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                      {entry.address_data.phone ? <span>{entry.address_data.phone}</span> : null}
                    </div>
                  </label>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" className="checkout-address-action-btn checkout-sheet-add-address" onClick={() => {
                setAddressPickerOpen(false);
                setShippingFormOpen(true);
              }}>
                <Plus data-icon="inline-start" aria-hidden="true" />
                Add new address
              </Button>
            </div>
          </div>,
          document.body
        ) : null}

        <Card className="cart-summary checkout-summary-panel">
          <h3>Order summary</h3>
          {cart.map((item) => {
            const imageUrl = cartItemImageUrl(item);
            const stlUrl = cartItemStlUrl(item);
            const hasStl = Boolean(stlUrl) || cartItemHasStl(item);
            return (
              <div className="summary-row summary-item-row" key={item.id}>
                <div className="summary-item-main">
                  {imageUrl ? (
                    <img className="summary-item-thumb" src={imageUrl} alt="" loading="lazy" />
                  ) : stlUrl ? (
                    <span className="summary-item-thumb summary-item-model" aria-hidden="true">
                      <Suspense fallback={<span className="summary-item-thumb placeholder" data-kind="stl"><ShoppingBag size={16} /></span>}>
                        <STLViewer
                          url={stlUrl}
                          color={item.stlColor || String(item.quote_options?.color || "Blue")}
                          colorHex={item.stlColorHex}
                          mode="product"
                          interactive={false}
                          autoRotate={false}
                          className="stl-viewer-static"
                          hideLoadingOverlay
                        />
                      </Suspense>
                    </span>
                  ) : (
                    <span className="summary-item-thumb placeholder" data-kind={hasStl ? "stl" : "item"} aria-hidden="true">
                      {hasStl ? "STL" : ""}
                    </span>
                  )}
                  <span className="summary-item-meta">
                    <span className="summary-item-name">{item.name}{item.quantity > 1 ? ` x${item.quantity}` : ""}</span>
                    {item.detail ? <small>{item.detail}</small> : null}
                  </span>
                </div>
                <span className="summary-item-price">{formatCurrency(item.price * item.quantity)}</span>
              </div>
            );
          })}
          <div className="summary-row"><span>Items subtotal</span><span id="checkout-subtotal">{formatCheckoutCurrency(subtotal)}</span></div>
          <div className="summary-row" id="checkout-shipping-row">
            <span>Delivery</span>
            <span id="checkout-shipping-cost">{selectedShippingRate ? shippingPriceLabel(selectedShippingRate.amount) : "Pending"}</span>
          </div>
          <div className="summary-row"><span>Taxable amount</span><span>{formatCheckoutCurrency(taxableAmount)}</span></div>
          {selectedTaxBreakdown?.enabled && selectedTaxBreakdown.tax_type === "cgst_sgst" ? (
            <>
              <div className="summary-row"><span>CGST ({selectedTaxBreakdown.cgst_rate}%)</span><span>{formatCheckoutCurrency(selectedTaxBreakdown.cgst)}</span></div>
              <div className="summary-row"><span>SGST ({selectedTaxBreakdown.sgst_rate}%)</span><span>{formatCheckoutCurrency(selectedTaxBreakdown.sgst)}</span></div>
            </>
          ) : selectedTaxBreakdown?.enabled && selectedTaxBreakdown.tax_type === "igst" ? (
            <div className="summary-row"><span>IGST ({selectedTaxBreakdown.igst_rate}%)</span><span>{formatCheckoutCurrency(selectedTaxBreakdown.igst)}</span></div>
          ) : (
            <div className="summary-row"><span>GST</span><span>{selectedShippingRate ? formatCheckoutCurrency(0) : "Pending"}</span></div>
          )}
          <div className="summary-total"><span>Total</span><span id="checkout-total">{formatCheckoutCurrency(total)}</span></div>
          <Button className="btn-block checkout-primary-action" type="submit" disabled={!canPlaceOrder || placingOrder}>
            {placingOrder ? "Redirecting..." : checkoutButtonLabel}
          </Button>
          <Button asChild variant="ghost" className="btn-block checkout-back-link">
            <Link to="/cart">
              <ChevronLeft data-icon="inline-start" aria-hidden="true" />
              Back to cart
            </Link>
          </Button>
          <div className="checkout-secure-note">
            <PackageCheck size={16} aria-hidden="true" />
            Secure checkout and protected order
          </div>
        </Card>
        <div className="checkout-mobile-cta">
          <strong>{formatCheckoutCurrency(total)}</strong>
          <Button type="submit" disabled={!canPlaceOrder || placingOrder}>
            {placingOrder ? "Redirecting..." : paymentMethod === "manual" ? "Place order" : "Continue"}
          </Button>
        </div>
      </form>
    </section>
  );
}

function AddressFields({
  value,
  onChange,
  errors,
  setErrors
}: {
  value: AddressFormState;
  onChange: (value: AddressFormState | ((current: AddressFormState) => AddressFormState)) => void;
  errors: AddressFieldErrors;
  setErrors: (value: AddressFieldErrors | ((current: AddressFieldErrors) => AddressFieldErrors)) => void;
}) {
  function patch<K extends keyof AddressFormState>(key: K, nextValue: AddressFormState[K]) {
    const cleanValue = (key === "phone" || key === "pincode")
      ? String(nextValue || "").replace(/\D+/g, "").slice(0, key === "phone" ? 10 : 6)
      : nextValue;
    onChange((current) => ({ ...current, [key]: cleanValue }));
    setErrors((current) => {
      if (!(key in current)) return current;
      const nextErrors = buildAddressErrors({ ...value, [key]: cleanValue });
      if (!nextErrors[key]) {
        const { [key]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: nextErrors[key] || "Required" };
    });
  }

  return (
    <div className="form-grid">
      <label className={errors.name ? "form-field-error" : undefined}>
        <span>Full Name</span>
        <input className={`form-control${errors.name ? " form-control-error" : ""}`} value={value.name} onChange={(event) => patch("name", event.currentTarget.value)} />
        {errors.name ? <span className="form-error-text">{errors.name}</span> : null}
      </label>
      <label className={errors.phone ? "form-field-error" : undefined}>
        <span>Phone</span>
        <input className={`form-control${errors.phone ? " form-control-error" : ""}`} type="tel" inputMode="numeric" maxLength={10} value={value.phone} onChange={(event) => patch("phone", event.currentTarget.value)} />
        {errors.phone ? <span className="form-error-text">{errors.phone}</span> : null}
      </label>
      <label className={`wide${errors.line1 ? " form-field-error" : ""}`}>
        <span>Address Line 1</span>
        <input className={`form-control${errors.line1 ? " form-control-error" : ""}`} value={value.line1} onChange={(event) => patch("line1", event.currentTarget.value)} />
        {errors.line1 ? <span className="form-error-text">{errors.line1}</span> : null}
      </label>
      <label className="wide">
        <span>Address Line 2</span>
        <input className="form-control" value={value.line2} onChange={(event) => patch("line2", event.currentTarget.value)} />
      </label>
      <label className={errors.city ? "form-field-error" : undefined}>
        <span>City</span>
        <input className={`form-control${errors.city ? " form-control-error" : ""}`} value={value.city} onChange={(event) => patch("city", event.currentTarget.value)} />
        {errors.city ? <span className="form-error-text">{errors.city}</span> : null}
      </label>
      <label>
        <span>State</span>
        <input className="form-control" value={value.state} onChange={(event) => patch("state", event.currentTarget.value)} />
      </label>
      <label className={errors.pincode ? "form-field-error" : undefined}>
        <span>Pincode</span>
        <input className={`form-control${errors.pincode ? " form-control-error" : ""}`} inputMode="numeric" maxLength={6} value={value.pincode} onChange={(event) => patch("pincode", event.currentTarget.value)} />
        {errors.pincode ? <span className="form-error-text">{errors.pincode}</span> : null}
      </label>
    </div>
  );
}
